/**
 * Right-edge notch mask — shared between the NotificationPanel and
 * ChatV2/ChatHost. Cuts a 48×93 "bite" out of the right edge of the
 * panel surface at a configurable Y offset so the page behind shows
 * through. Visual signature for both panels — keeps them feeling like
 * one family.
 *
 * Implementation: a two-layer CSS mask. Layer 1 (linear-gradient) makes
 * the whole panel visible; layer 2 (the notch SVG) is composited
 * subtractively so the notch area becomes transparent.
 *
 *   mask-composite: subtract       (CSS Masking Module Level 1)
 *   -webkit-mask-composite: source-out (WebKit legacy fallback)
 *
 * Animating Y: browsers DO transition mask-position; just put a CSS
 * transition on `mask-position` / `-webkit-mask-position` and toggle
 * the offset in inline style — the notch slides up/down.
 */

// The notch outline path (viewBox 0 0 48 93). Exported so the stroke-only
// border overlay (e.g. ChatV2's bordered notch) can trace the EXACT same
// shape as this mask — single source of truth, pixel-perfect overlap.
export const NOTCH_PATH =
  "M48 0C48 7.43319 44.1728 23.8722 32.7081 29.6637C25.4142 33.3483 18.2369 35.1929 12.5534 36.6335C9.71915 37.352 7.24093 37.9736 5.32034 38.675C4.35959 39.0259 3.52257 39.4012 2.84123 39.8291C2.1687 40.2515 1.60184 40.7523 1.22221 41.3916C0.263209 43.0064 -0.039956 44.825 0.00412007 46.5C-0.039956 48.175 0.263209 49.9936 1.22221 51.6084C1.60184 52.2477 2.1687 52.7485 2.84123 53.1709C3.52257 53.5988 4.35959 53.9741 5.32034 54.325C7.24093 55.0264 9.71915 55.648 12.5534 56.3665C18.2369 57.8071 25.4142 59.6517 32.7081 63.3363C44.1726 69.1277 47.9999 85.5667 48 93L48 46.5L48 0Z";

const NOTCH_SVG = `<svg xmlns='http://www.w3.org/2000/svg' width='48' height='93' viewBox='0 0 48 93'><path d='${NOTCH_PATH}' fill='black'/></svg>`;

export const NOTCH_MASK_DATA_URI =
  "data:image/svg+xml;utf8," + encodeURIComponent(NOTCH_SVG);

// Rendered size — keeps the 48:93 aspect ratio of the source SVG so
// the bite shape stays correct. 32 wide × 62 tall reads as a small
// premium notch (large 48 felt too eaten-into per Viktor 2026-05-29).
export const NOTCH_WIDTH = 32;
export const NOTCH_HEIGHT = 62;

// Narrow (Sidebar-style) notch — viewBox 0 0 29 51. This is the FILL mask
// companion to the refined stroke border used by ChatV2 (the border path is
// offset ~½ stroke outward from this so the 1.5px stroke sits on the cut
// edge). Same shape family as the Sidebar's notch so the two panels match.
export const NARROW_NOTCH_VIEW_W = 29;
export const NARROW_NOTCH_VIEW_H = 51;
export const NARROW_NOTCH_PATH =
  "M28.5 0C28.5 1.96671 27.9567 5.17105 26.5898 8.26953C25.2229 11.3682 23.061 14.2978 19.8652 15.8154C15.6632 17.8109 11.5233 18.8123 8.20703 19.6025C6.56357 19.9942 5.09006 20.342 3.94141 20.7363C3.36521 20.9342 2.84157 21.1524 2.40332 21.4111C1.97141 21.6661 1.56871 21.9905 1.29004 22.4316C0.742633 23.2983 0.529262 24.2504 0.50293 25.1377V25.8623C0.529262 26.7496 0.742634 27.7017 1.29004 28.5684C1.56871 29.0095 1.97141 29.3339 2.40332 29.5889C2.84157 29.8476 3.36521 30.0658 3.94141 30.2637C5.09006 30.658 6.56357 31.0058 8.20703 31.3975C11.5233 32.1877 15.6632 33.1891 19.8652 35.1846C23.061 36.7022 25.2229 39.6318 26.5898 42.7305C27.8713 45.6353 28.4285 48.6333 28.4932 50.6172L28.5 51H29.5V0H28.5Z";

function buildNotchUri(path: string, viewW: number, viewH: number): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${viewW}' height='${viewH}' viewBox='0 0 ${viewW} ${viewH}' preserveAspectRatio='none'><path d='${path}' fill='black'/></svg>`;
  return "data:image/svg+xml;utf8," + encodeURIComponent(svg);
}

interface NotchMaskOptions {
  /** Distance from the panel's top edge to the top of the notch (px). */
  topPx: number;
  /** Optional CSS transition duration for the position slide (ms). */
  transitionMs?: number;
  /** Notch fill path (defaults to the wide 48×93 notch). */
  path?: string;
  /** viewBox dimensions of `path`. */
  viewW?: number;
  viewH?: number;
  /** Rendered notch size in px (defaults to NOTCH_WIDTH × NOTCH_HEIGHT). */
  width?: number;
  height?: number;
}

/**
 * Returns the CSS style chunk that paints the notch cutout on the right
 * edge of the panel. Merge into the panel's inline style. The notch
 * cuts THROUGH the surface so whatever's behind the panel is visible
 * inside the notch.
 */
export function getNotchMaskStyle({
  topPx,
  transitionMs = 0,
  path = NOTCH_PATH,
  viewW = 48,
  viewH = 93,
  width = NOTCH_WIDTH,
  height = NOTCH_HEIGHT,
}: NotchMaskOptions): React.CSSProperties {
  const uri = buildNotchUri(path, viewW, viewH);
  const transition = transitionMs > 0
    ? `mask-position ${transitionMs}ms cubic-bezier(0.22,1,0.36,1), -webkit-mask-position ${transitionMs}ms cubic-bezier(0.22,1,0.36,1)`
    : undefined;
  return {
    WebkitMaskImage: `linear-gradient(#000, #000), url("${uri}")`,
    maskImage: `linear-gradient(#000, #000), url("${uri}")`,
    WebkitMaskSize: `100% 100%, ${width}px ${height}px`,
    maskSize: `100% 100%, ${width}px ${height}px`,
    WebkitMaskPosition: `center, right ${topPx}px`,
    maskPosition: `center, right ${topPx}px`,
    WebkitMaskRepeat: "no-repeat, no-repeat",
    maskRepeat: "no-repeat, no-repeat",
    WebkitMaskComposite: "source-out",
    maskComposite: "subtract",
    transition,
  };
}
